// components/CardComprobantes.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  IconButton,
  Tooltip,
  TablePagination,
  TableFooter,
  useTheme
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import moment from 'moment';
import CardExpandible from './CardExpandible';
import { getFacturasResumenSucursal } from '../services/facturas.service';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { verEnviarPDFenvasado } from '../../../../services/services';

const formatNumber = (value) => {
  if (!value && value !== 0) return '';
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '';
  
  const [entero, decimal] = numValue.toFixed(2).split('.');
  const enteroFormateado = entero.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  
  return `${enteroFormateado},${decimal}`;
};

const CardComprobantes = ({ 
  sucursal, 
  desde, 
  hasta, 
  loading = false,
  triggerRecarga = 0
}) => {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [estadisticas, setEstadisticas] = useState({
    total: 0,
    cantidad: 0,
    totalFacturasA: 0,
    totalFacturasB: 0,
    anuladas: 0
  });
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [expandedRows, setExpandedRows] = useState({});

  // Extraer datos de sucursal
  const sucursalId = sucursal?.id;
  const sucursalNombre = sucursal?.nombre;

  // Efecto para cargar datos
  useEffect(() => {
    if (sucursalId && desde && hasta) {
      cargarDatos();
    }
  }, [sucursalId, desde, hasta, triggerRecarga]);

  const cargarDatos = async () => {
    setLoadingData(true);
    setError(null);
    
    try {
      const response = await getFacturasResumenSucursal(sucursalId, desde, hasta);
      
      
      if (Array.isArray(response)) {
        setData(response);
        calcularEstadisticas(response);
        setPage(0);
      } else if (response.success) {
        setData(response.data || []);
        calcularEstadisticas(response.data || []);
        setPage(0);
      } else {
        setError(response.error || 'Error al cargar los datos');
      }
    } catch (error) {
      console.error('Error al cargar facturas:', error);
      setError(error.message || 'Error al cargar los datos de facturas');
    } finally {
      setLoadingData(false);
    }
  };

  const calcularEstadisticas = (facturas) => {
    const total = facturas.reduce((sum, f) => sum + (parseFloat(f.importe_total) || 0), 0);
    
    // Contar por tipo de comprobante (AFIP)
    const facturasA = facturas.filter(f => f.codigo_comprobante_afip === 1).length;
    const facturasB = facturas.filter(f => f.codigo_comprobante_afip === 6).length;
    const anuladas = facturas.filter(f => f.id_estado === 260).length;
    
    setEstadisticas({
      total: total,
      cantidad: facturas.length,
      totalFacturasA: facturasA,
      totalFacturasB: facturasB,
      anuladas: anuladas
    });
  };

  // Manejadores de paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setExpandedRows({});
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setExpandedRows({});
  };

  const toggleExpandRow = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Obtener estado del comprobante
  const getEstadoInfo = (idEstado) => {
    switch(idEstado) {
      case 261: return { label: 'Emitido', color: 'success', icon: <CheckCircleIcon fontSize="small" /> };
      case 262: return { label: 'Pendiente', color: 'warning', icon: <PendingIcon fontSize="small" /> };
      case 263: return { label: 'Anulado', color: 'error', icon: <CancelIcon fontSize="small" /> };
      default: return { label: 'Desconocido', color: 'default', icon: null };
    }
  };

  // Obtener tipo de comprobante
  const getTipoComprobante = (codigo) => {
    switch(codigo) {
      case 1: return 'Factura A';
      case 6: return 'Factura B';
      default: return `Tipo ${codigo}`;
    }
  };

  // Paginación
  const datosPaginados = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;

  // Loading
  if (loading || loadingData) {
    return (
      <CardExpandible title="Comprobantes" icon={ReceiptIcon}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress size={40} sx={{ color: '#757575' }} />
        </Box>
      </CardExpandible>
    );
  }

  // Validaciones
  if (!sucursalId || !desde || !hasta) {
    let mensaje = '';
    if (!sucursalId) mensaje = 'Selecciona una sucursal';
    else if (!desde || !hasta) mensaje = 'Selecciona un rango de fechas';
    
    return (
      <CardExpandible title="Comprobantes" icon={ReceiptIcon}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            {mensaje}
          </Typography>
        </Box>
      </CardExpandible>
    );
  }

  if (error) {
    return (
      <CardExpandible title="Comprobantes" icon={ReceiptIcon}>
        <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
      </CardExpandible>
    );
  }

  if (!data || data.length === 0) {
    return (
      <CardExpandible title="Comprobantes" icon={ReceiptIcon}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            No hay comprobantes en este período
          </Typography>
        </Box>
      </CardExpandible>
    );
  }

  return (
    <CardExpandible 
      title="Comprobantes" 
      icon={ReceiptIcon}
      subtitle={`${estadisticas.cantidad} comprobantes · $${estadisticas.total.toLocaleString()}`}
      // actionButtons={
      //   <>
      //     <Tooltip title="Descargar todos">
      //       <IconButton size="small" sx={{ color: '#757575' }}>
      //         <DownloadIcon fontSize="small" />
      //       </IconButton>
      //     </Tooltip>
      //   </>
      // }
    >
      <Box>
        {/* Estadísticas */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={3}>
            <Box sx={{ 
              textAlign: 'center',
              // p: 1,
              bgcolor: '#f5f5f5',
              borderRadius: 1
            }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: '#1a1a1a', fontSize: '1.1rem' }}>
                ${formatNumber(estadisticas.total)}
              </Typography>
              <Typography variant="caption" color="textSecondary">Total</Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box sx={{ 
              textAlign: 'center',
              // p: 1,
              bgcolor: '#e8f5e9',
              borderRadius: 1
            }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: '#2e7d32', fontSize: '1.1rem' }}>
                {estadisticas.totalFacturasA}
              </Typography>
              <Typography variant="caption" color="textSecondary">Factura A</Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box sx={{ 
              textAlign: 'center',
              // p: 1,
              bgcolor: '#e3f2fd',
              borderRadius: 1
            }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: '#1976d2', fontSize: '1.1rem' }}>
                {estadisticas.totalFacturasB}
              </Typography>
              <Typography variant="caption" color="textSecondary">Factura B</Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box sx={{ 
              textAlign: 'center',
              // p: 1,
              bgcolor: '#e3f2fd',
              borderRadius: 1
            }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: '#1976d2', fontSize: '1.1rem' }}>
                {estadisticas.anuladas}
              </Typography>
              <Typography variant="caption" color="textSecondary">Anuladas</Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2 }} />

        {/* Tabla */}
        <TableContainer component={Paper} variant="outlined" sx={{ borderColor: '#e8e8e8' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Comprobante</TableCell>
                <TableCell sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Tipo</TableCell>
                <TableCell align="right" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Total</TableCell>
                <TableCell align="center" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.75rem' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {datosPaginados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                      No hay registros para mostrar
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                datosPaginados.map((item, index) => {
                  const globalIndex = page * rowsPerPage + index;
                  const key = `factura-${globalIndex}`;
                  const estadoInfo = getEstadoInfo(item.id_estado);
                  const tipoComprobante = getTipoComprobante(item.codigo_comprobante_afip);
                  
                  return (
                    <React.Fragment key={key}>
                      <TableRow hover>
                        <TableCell sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                          {item.pto_venta}-{item.nro_comprobante}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.7rem' }}>
                          {item.fecha_emision ? moment(item.fecha_emision).format('DD-MM-YYYY') : '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.7rem' }}>
                          {item.razon_social || '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.7rem' }}>
                          {tipoComprobante || '-'}
                        </TableCell>
                        
                        <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                          ${formatNumber(item.importe_total || 0)}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Ver factura">
                            <IconButton 
                              size="small" 
                              onClick={() =>
                                verEnviarPDFenvasado(item.id_factura, 0)
                              }
                            >
                              <VisibilityIcon color='primary'/> 
                            </IconButton>
                          </Tooltip>
                          {item.id_estado===260?(
                          <Tooltip title="Ver nota de crédito">
                            <IconButton 
                              size="small" 
                              onClick={() =>
                                verEnviarPDFenvasado(item.id_nc, 0)
                              }
                            >
                              <VisibilityOffIcon color='secondary' /> 
                            </IconButton>
                          </Tooltip>
                          ):null}
                          
                        </TableCell>
                      </TableRow>
                      {/* Fila expandida */}
                      {/* <TableRow>
                        <TableCell colSpan={8} sx={{ py: 0 }}>
                          <Box sx={{ 
                            display: expandedRows[key] ? 'block' : 'none'
                          }}>
                            <Box sx={{ 
                              p: 1.5, 
                              bgcolor: '#fafafa',
                              borderTop: '1px solid #e8e8e8'
                            }}>
                              <Grid container spacing={1}>
                                <Grid item xs={12}>
                                  <Box sx={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                    gap: 0.5
                                  }}>
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>N° Comprobante:</span>
                                      <span>{item.pto_venta}-{String(item.nro_comprobante).padStart(8, '0')}</span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>UUID:</span>
                                      <span style={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>{item.uuid || '-'}</span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>Cliente:</span>
                                      <span>{item.razon_social || '-'}</span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>Tipo:</span>
                                      <span>{tipoComprobante}</span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>Fecha Emisión:</span>
                                      <span>{item.fecha_emision ? moment(item.fecha_emision).format('DD-MM-YYYY') : '-'}</span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>CUIT/DNI:</span>
                                      <span>{item.cuit_dni || '-'}</span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>Importe Neto:</span>
                                      <span>${parseFloat(item.importe_neto || 0).toLocaleString()}</span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>IVA:</span>
                                      <span>${parseFloat(item.importe_iva || 0).toLocaleString()}</span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>Importe Total:</span>
                                      <span style={{ fontWeight: 500 }}>${parseFloat(item.importe_total || 0).toLocaleString()}</span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>Estado:</span>
                                      <span>{estadoInfo.label}</span>
                                    </Typography>
                                    {item.cae && (
                                      <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                        <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>CAE:</span>
                                        <span style={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>{item.cae}</span>
                                      </Typography>
                                    )}
                                    {item.id_pedido && (
                                      <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                        <span style={{ fontWeight: 600, color: '#424242', minWidth: 100 }}>N° Pedido:</span>
                                        <span>{item.id_pedido}</span>
                                      </Typography>
                                    )}
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow> */}
                    </React.Fragment>
                  );
                })
              )}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={8} />
                </TableRow>
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  colSpan={8}
                  count={data.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="Filas por página:"
                  labelDisplayedRows={({ from, to, count }) => 
                    `${from}-${to} de ${count}`
                  }
                  sx={{
                    '.MuiTablePagination-select': {
                      fontSize: '0.8rem'
                    },
                    '.MuiTablePagination-displayedRows': {
                      fontSize: '0.8rem'
                    }
                  }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>

        {/* Información adicional */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="textSecondary">
            Sucursal: {sucursalNombre || sucursalId}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Total comprobantes: {data.length} · Actualizado: {moment().format('DD-MM-YYYY HH:mm')}
          </Typography>
        </Box>
      </Box>
    </CardExpandible>
  );
};

export default CardComprobantes;